// mantine
import { notifications } from '@mantine/notifications';
// utils
import type { TV3Project } from '@newo/api/gateway';
import { useSleep } from '@/hooks/useSleep.ts';
import {
    useCreateProjectMutation,
    useProjectsQuery,
    useDeleteProjectMutation,
    useUpdateProjectMutation,
    useV3ForceUpdateProjectMutation
} from '@/services/designer/projects/projects.queries';
import { openConfirmDeleteModal } from '@/utils/ui';

const DELAY_REFETCH_REQUEST = 3000;

export const useProjectsV2 = () => {
    const {
        data: projects,
        isLoading: isProjectsLoading,
        refetch
    } = useProjectsQuery(true);
    const { sleep } = useSleep();
    const { mutateAsync: createProject } = useCreateProjectMutation({
        onSuccess: () => {
            refetch();
        }
    });
    const { mutateAsync: deleteProject } = useDeleteProjectMutation({
        onSuccess: () => {
            refetch();
        }
    });
    const { mutateAsync: updateProject } = useUpdateProjectMutation({
        onSuccess: () => {
            refetch();
        }
    });
    const { mutateAsync: forceUpdateProject } = useV3ForceUpdateProjectMutation(
        {
            onSuccess: async () => {
                notifications.show({
                    title: 'Project Updated',
                    message: 'Project updated successfully',
                    color: 'green'
                });
                await sleep(DELAY_REFETCH_REQUEST);
                refetch();
            }
        }
    );

    const handleDelete = (project: TV3Project): void => {
        openConfirmDeleteModal({
            title: `Delete ${project.idn} project?`,
            children: (
                <>Are you sure you want to delete {project.idn} project?</>
            ),
            confirmLabel: 'Delete Project',
            onConfirm: () => {
                void deleteProject({ projectId: project.id });
            }
        });
    };

    return {
        projects,
        isProjectsLoading,
        onProjectCreate: createProject,
        onProjectDelete: handleDelete,
        onProjectUpdate: updateProject,
        onProjectForceUpdate: forceUpdateProject
    };
};
